import { Button, Form, type FormProps, Modal, message, Select } from 'antd';
import type React from 'react';
import { requiredArrayRule } from 'src/helpers/form';
import { useTriggerMappingResultsAction } from 'src/hooks/data/partner-games';
import { useGetPlatformsData } from 'src/hooks/data/platforms';
import type { TriggerPartnerGamesMappingResultBody } from 'src/types/partner-games';
import { BrandsSelect } from './brands-select/brands-select';

interface LaunchMappingModalProps {
  isOpen: boolean;
  close: () => void;
}

export const LaunchMappingModal: React.FC<LaunchMappingModalProps> = ({ isOpen, close }) => {
  const { data, isInProgress } = useGetPlatformsData();
  const { triggerMappingResults, isInProgress: isTriggerMappingResultsInProgress } = useTriggerMappingResultsAction();
  const [form] = Form.useForm();
  const platformsWatcher = Form.useWatch<string[]>('platforms', form);

  const onCancel = () => {
    close();
    form.resetFields();
  };

  const onValuesChange: FormProps<{ platforms: string[]; brands: string[] }>['onValuesChange'] = (changed, values) => {
    if (changed.platforms && !values.brands.includes('all')) {
      form.setFieldValue(
        'brands',
        values.brands.filter((key) => changed.platforms?.includes(key.split('***')[0])),
      );
    }
  };

  const onFinish: FormProps<{ platforms: string[]; brands: string[] }>['onFinish'] = (values) => {
    const payload: TriggerPartnerGamesMappingResultBody = [];

    if (values.brands.includes('all')) {
      const platforms = values.platforms.length
        ? values.platforms
        : (data || []).map(({ zingPlatformName }) => zingPlatformName);

      platforms.forEach((platform) => {
        const foundPlatform = data?.find(({ zingPlatformName }) => zingPlatformName === platform);

        if (foundPlatform) {
          foundPlatform.brands.forEach(({ externalBrandName }) => {
            payload.push({
              zingPlatformName: platform,
              externalBrandName,
            });
          });
        }
      });
    } else {
      values.brands.forEach((key) => {
        const [zingPlatformName, externalBrandName] = key.split('***');

        payload.push({ zingPlatformName, externalBrandName });
      });
    }

    triggerMappingResults(payload, {
      onSuccess: () => {
        message.success(`Mapping has been triggered on ${payload.length} ${payload.length === 1 ? 'brand' : 'brands'}`);
        onCancel();
      },
      onError: () => {
        message.error('Mapping has not been triggered');
      },
    });
  };

  const initialValues = {
    platforms: [],
    brands: [],
  };

  return (
    <Modal
      width={616}
      title="Launch Mapping"
      footer={[
        <Button key="back" size="large" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          onClick={() => {
            form.submit();
          }}
          loading={isTriggerMappingResultsInProgress}
        >
          Launch
        </Button>,
      ]}
      open={isOpen}
      onCancel={onCancel}
    >
      <Form
        form={form}
        initialValues={initialValues}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
        layout="vertical"
      >
        <Form.Item name="platforms" label="Platforms for Mapping" className="mb-16">
          <Select
            mode="multiple"
            placeholder="Select Platforms"
            allowClear
            loading={isInProgress}
            options={data?.map(({ zingPlatformName }) => ({
              value: zingPlatformName,
              label: zingPlatformName,
            }))}
          />
        </Form.Item>

        <Form.Item name="brands" label="Brands for Mapping" className="mb-0" rules={[requiredArrayRule()]}>
          <BrandsSelect
            loading={isInProgress}
            options={data
              ?.filter(({ zingPlatformName }) =>
                platformsWatcher?.length ? platformsWatcher.includes(zingPlatformName) : true,
              )
              .flatMap(({ zingPlatformName, brands }) =>
                brands.map(({ externalBrandName }) => ({
                  value: `${zingPlatformName}***${externalBrandName}`,
                  label: externalBrandName,
                })),
              )
              .sort((a, b) => a.label.localeCompare(b.label))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
